import { z } from 'zod';
import {
  AgentSessionView,
  EnsureWorkItem,
  EnsureWorkResult,
  EvidenceView,
  FinishAgentSession,
  ProgressEntryView,
  RecordProgress,
  SetBlocker,
  StartAgentSession,
  TransitionWork,
  WorkCandidate,
  WorkItemListItem,
  WorkItemView,
  AttachEvidence,
} from './work.js';
import { ConfirmRequest, PrepareRequest, PrepareRequestResult, RequestListItem, RequestView, SubmitRequest, TransitionRequest } from './requests.js';
import { AddComment, CommentView } from './collaboration.js';
import { LinkCodeFields, LinkCodeRefinement, LinkCodeResult } from './delivery.js';
import { QueryReport, ReportResult } from './reports.js';
import { Priority } from './enums.js';
import { Uuid, Version } from './common.js';
import { routes } from './routes.js';
import type { RouteDefinition } from './route.js';

/** Client-generated idempotency key carried by every mutating tool. */
const RequestId = z.string().min(8).max(100).describe('Client-generated idempotency key, stable across retries of the same call');

export const McpScope = z.enum(['work:read', 'work:write', 'requests:read', 'requests:write', 'reports:read']);
export type McpScope = z.infer<typeof McpScope>;

export interface McpToolDefinition {
  name: string;
  title: string;
  description: string;
  input: z.ZodObject;
  output: z.ZodType;
  /** The connection needs at least one of these scopes. */
  scope: McpScope | readonly McpScope[];
  annotations: { readOnlyHint: boolean; destructiveHint: boolean; idempotentHint: boolean; openWorldHint: boolean };
}

const read = { readOnlyHint: true, destructiveHint: false, idempotentHint: true, openWorldHint: false };
const write = { readOnlyHint: false, destructiveHint: false, idempotentHint: true, openWorldHint: false };

// ---------------------------------------------------------------- Operation gateway (UI parity)

/**
 * Every REST operation the web app uses is reachable from MCP through four generic tools, so agents
 * can do what the person can do in the UI. Reads are GET operations; everything else is a write.
 * Scopes follow the operation's area; roles and record access are enforced by the shared services.
 */
export type McpOperationAccess = 'read' | 'write';

const REQUEST_TAGS = new Set(['requests', 'intake']);
const SHARED_TAGS = new Set(['me', 'notifications', 'views', 'jobs', 'comments', 'evidence', 'attachments', 'translations', 'runtime']);

export function mcpOperationAccess(route: RouteDefinition): { access: McpOperationAccess; scopes: McpScope[] } {
  const access: McpOperationAccess = route.method === 'GET' ? 'read' : 'write';
  if (REQUEST_TAGS.has(route.tag)) return { access, scopes: [access === 'read' ? 'requests:read' : 'requests:write'] };
  // Report queries and exports read data even when they are POST requests.
  if (route.tag === 'reports') return { access, scopes: ['reports:read'] };
  if (SHARED_TAGS.has(route.tag)) return { access, scopes: access === 'read' ? ['work:read', 'requests:read'] : ['work:write', 'requests:write'] };
  return { access, scopes: [access === 'read' ? 'work:read' : 'work:write'] };
}

/** Registry operations reachable through the gateway. */
export function mcpOperations(): Array<[string, RouteDefinition]> {
  return Object.entries(routes as Record<string, RouteDefinition>).sort(([a], [b]) => a.localeCompare(b));
}

const OperationId = z.string().min(1).max(80).describe('operationId from list_operations, for example createProject');
const OperationArgs = {
  operationId: OperationId,
  params: z.record(z.string(), z.string()).optional().describe('Path parameters, for example { "id": "<uuid>" }'),
  query: z.record(z.string(), z.string()).optional().describe('Query string parameters as strings'),
};
const OperationSummary = z.object({
  operationId: z.string(),
  method: z.string(),
  path: z.string(),
  summary: z.string(),
  tag: z.string(),
  access: z.enum(['read', 'write']),
  idempotent: z.boolean(),
});
const OperationResult = z.object({ data: z.unknown(), nextCursor: z.string().nullable().optional() });
const readAny: McpScope[] = ['work:read', 'requests:read', 'reports:read'];
const writeAny: McpScope[] = ['work:write', 'requests:write', 'reports:read'];

export const ContextView = z.object({
  user: z.object({ id: Uuid, displayName: z.string(), locale: z.string(), timezone: z.string() }),
  capabilities: z.array(z.string()),
  scopes: z.array(McpScope),
  projects: z.array(z.object({ id: Uuid, key: z.string(), name: z.string() })),
  teams: z.array(z.object({ id: Uuid, name: z.string(), role: z.string() })),
  activeSessions: z.array(z.object({ id: Uuid, client: z.string(), clientSessionRef: z.string(), workItemKeys: z.array(z.string()) })),
  guidance: z.string(),
});

export const mcpTools = {
  get_context: {
    name: 'get_context',
    title: 'Get work-tracking context',
    description:
      'Returns the authenticated person, capabilities, granted scopes, accessible projects/teams and active agent sessions. Call first in a session. Never returns an organization-wide user list.',
    input: z.object({ clientSessionRef: z.string().max(200).optional() }),
    output: ContextView,
    scope: 'work:read',
    annotations: read,
  },
  search_work: {
    name: 'search_work',
    title: 'Search work items',
    description:
      'Permission-filtered candidate search with explainable association signals. Results are suggestions only: similarity never auto-links a session to a task. Max 20 hits.',
    input: z.object({
      query: z.string().min(1).max(500),
      projectId: Uuid.optional(),
      repositoryFullName: z.string().max(200).optional(),
      includeDone: z.boolean().optional(),
    }),
    output: z.object({ hits: z.array(WorkCandidate.extend({ phase: z.string(), updatedAt: z.string() })) }),
    scope: 'work:read',
    annotations: read,
  },
  get_work_item: {
    name: 'get_work_item',
    title: 'Get a work item',
    description: 'Current work item state, permitted recent progress (max 20 entries) and evidence. Accepts a UUID or display key such as VAM-42.',
    input: z.object({ idOrKey: z.string().min(1).max(60) }),
    output: z.object({ workItem: WorkItemView, recentProgress: z.array(ProgressEntryView), evidence: z.array(EvidenceView) }),
    scope: 'work:read',
    annotations: read,
  },
  search_requests: {
    name: 'search_requests',
    title: 'Search requests',
    description: 'Authorized requests and their functional state. Max 20 hits.',
    input: z.object({ query: z.string().max(500).optional(), status: z.string().max(100).optional(), mine: z.boolean().optional() }),
    output: z.object({ hits: z.array(RequestListItem) }),
    scope: 'requests:read',
    annotations: read,
  },
  get_request: {
    name: 'get_request',
    title: 'Get a request',
    description: 'Request detail with functional progress, recent comments and requester feedback. Accepts a UUID or key such as REQ-12.',
    input: z.object({ idOrKey: z.string().min(1).max(60) }),
    output: z.object({ request: RequestView, recentComments: z.array(CommentView) }),
    scope: 'requests:read',
    annotations: read,
  },
  ensure_work_item: {
    name: 'ensure_work_item',
    title: 'Ensure a work item',
    description:
      'Resolve an explicit key or stored session/branch/PR binding, or create one coherent unit of work idempotently by workIntentId. Ambiguity returns needs_selection with candidates and makes no hidden choice. Persist workIntentId locally before the first call.',
    input: EnsureWorkItem.extend({ requestId: RequestId }),
    output: EnsureWorkResult,
    scope: 'work:write',
    annotations: write,
  },
  start_work_session: {
    name: 'start_work_session',
    title: 'Start or resume a work session',
    description: 'Start or resume an agent session for the authenticated person. Unique per person, client and clientSessionRef.',
    input: StartAgentSession.extend({ requestId: RequestId }),
    output: AgentSessionView,
    scope: 'work:write',
    annotations: write,
  },
  record_progress: {
    name: 'record_progress',
    title: 'Record progress',
    description:
      'Append a bounded milestone summary (what changed, why, how, checks actually executed with claimBasis, results, pending work). Append-only; never overwrites human edits. Agent-reported checks stay distinct from CI-observed results.',
    input: RecordProgress.extend({ sessionId: Uuid, requestId: RequestId }),
    output: ProgressEntryView,
    scope: 'work:write',
    annotations: write,
  },
  set_work_phase: {
    name: 'set_work_phase',
    title: 'Request a work phase transition',
    description:
      'Validated transition under the completion policy. Cannot assert unproven production delivery: PRODUCTION work reaches DONE only from verified deployment evidence.',
    input: TransitionWork.extend({ workItemId: Uuid, requestId: RequestId }),
    output: WorkItemView,
    scope: 'work:write',
    annotations: write,
  },
  set_blocker: {
    name: 'set_blocker',
    title: 'Open or resolve a blocker',
    description: 'Open a blocker with a reason (optionally preventing completion) or resolve one.',
    input: z.object({ workItemId: Uuid, requestId: RequestId, blocker: SetBlocker }),
    output: WorkItemView,
    scope: 'work:write',
    annotations: write,
  },
  attach_evidence: {
    name: 'attach_evidence',
    title: 'Attach evidence',
    description: 'Add a source reference. The server assigns AGENT_REPORTED basis; only trusted adapters can produce PROVIDER_VERIFIED evidence.',
    input: AttachEvidence.extend({ requestId: RequestId }),
    output: EvidenceView,
    scope: 'work:write',
    annotations: write,
  },
  link_code: {
    name: 'link_code',
    title: 'Link code to a work item',
    description:
      'Link the code behind a work item so its Code tab opens it on GitHub. Pass the repository (owner/repo from the git remote) with the branch you work on (links its pull requests and the commits ahead of the default branch), pushed commit SHAs, PR numbers and/or GitHub URLs. Call it after creating or updating the work item and after each push or PR. Unpushed commits and unknown repositories are reported in skipped, never as errors. Idempotent.',
    input: LinkCodeFields.extend({ workItemId: z.string().min(1).max(60).describe('Work item UUID or key such as VAM-42'), requestId: RequestId }).refine(LinkCodeRefinement.check, LinkCodeRefinement.message),
    output: LinkCodeResult,
    scope: 'work:write',
    annotations: write,
  },
  finish_work_session: {
    name: 'finish_work_session',
    title: 'Finish a work session',
    description: 'End the session and record a handoff. Ending a session never marks work done.',
    input: FinishAgentSession.extend({ sessionId: Uuid, requestId: RequestId }),
    output: AgentSessionView,
    scope: 'work:write',
    annotations: write,
  },
  prepare_request: {
    name: 'prepare_request',
    title: 'Prepare a request draft',
    description:
      'Create or update a structured request draft as the authenticated requester. Returns missing fields and authorized duplicate candidates. Unknown impact is valid. Does not submit.',
    input: PrepareRequest.extend({ requestId: RequestId }),
    output: PrepareRequestResult,
    scope: 'requests:write',
    annotations: write,
  },
  submit_request: {
    name: 'submit_request',
    title: 'Submit a reviewed request',
    description: 'Submit the draft version the person reviewed. Returns the canonical request URL.',
    input: SubmitRequest.extend({ requestId: RequestId }),
    output: RequestView,
    scope: 'requests:write',
    annotations: write,
  },
  confirm_request: {
    name: 'confirm_request',
    title: 'Confirm or report a resolved request',
    description: 'Record the authenticated requester’s CONFIRMED or PERSISTS response for the displayed fulfillment generation.',
    input: ConfirmRequest.extend({ id: Uuid, requestId: RequestId }),
    output: RequestView,
    scope: 'requests:write',
    annotations: write,
  },
  triage_request: {
    name: 'triage_request',
    title: 'Triage a request',
    description: 'Apply an allowed transition, assignment or priority change, each checked against the person’s capabilities.',
    input: z.object({
      id: Uuid,
      requestId: RequestId,
      expectedVersion: Version,
      transition: TransitionRequest.omit({ expectedVersion: true }).optional(),
      assigneeId: Uuid.nullable().optional(),
      priority: Priority.optional(),
    }),
    output: RequestView,
    scope: 'requests:write',
    annotations: write,
  },
  add_comment: {
    name: 'add_comment',
    title: 'Comment',
    description: 'Comment on an accessible request or work item.',
    input: AddComment.extend({ requestId: RequestId }),
    output: CommentView,
    scope: 'work:write',
    annotations: write,
  },
  query_report: {
    name: 'query_report',
    title: 'Query a report',
    description: 'Execute an allowlisted report. Tier and row-level permissions are identical to the web UI and CSV export.',
    input: QueryReport,
    output: ReportResult,
    scope: 'reports:read',
    annotations: read,
  },
  list_operations: {
    name: 'list_operations',
    title: 'List available operations',
    description:
      'Lists the web app operations this connection can use (everything a person can do in the UI: projects, teams, work, requests, comments, notifications, views, reports, administration). Use it when no dedicated tool fits, then describe_operation and read_operation or write_operation.',
    input: z.object({ tag: z.string().max(40).optional().describe('Filter by area, for example projects'), search: z.string().max(100).optional().describe('Filter by words in the operationId or summary') }),
    output: z.object({ operations: z.array(OperationSummary) }),
    scope: readAny,
    annotations: read,
  },
  describe_operation: {
    name: 'describe_operation',
    title: 'Describe an operation',
    description: 'Returns the JSON Schemas of an operation\'s path parameters, query and body, so you can call it correctly.',
    input: z.object({ operationId: OperationId }),
    output: OperationSummary.extend({ params: z.unknown().nullable(), query: z.unknown().nullable(), body: z.unknown().nullable() }),
    scope: readAny,
    annotations: read,
  },
  read_operation: {
    name: 'read_operation',
    title: 'Run a read operation',
    description: 'Runs a read-only (GET) operation as the signed-in person, with the same permissions as the web app. Collections return data plus nextCursor; pass it back as query.cursor for the next page.',
    input: z.object(OperationArgs),
    output: OperationResult,
    scope: readAny,
    annotations: read,
  },
  write_operation: {
    name: 'write_operation',
    title: 'Run a write operation',
    description:
      'Runs a create, update or other changing operation as the signed-in person, with the same validation, permissions and conflict checks as the web app. Show the person what will change and get confirmation first. requestId makes retries safe for operations that support idempotency.',
    input: z.object({ ...OperationArgs, body: z.unknown().optional().describe('JSON body as described by describe_operation'), requestId: RequestId }),
    output: OperationResult,
    scope: writeAny,
    annotations: { readOnlyHint: false, destructiveHint: true, idempotentHint: false, openWorldHint: false },
  },
} satisfies Record<string, McpToolDefinition>;

/** Whether a connection with these scopes may use a tool. */
export function mcpToolAllowed(tool: Pick<McpToolDefinition, 'scope'>, scopes: ReadonlySet<string>): boolean {
  const needed = typeof tool.scope === 'string' ? [tool.scope] : tool.scope;
  return needed.some((s) => scopes.has(s));
}

export type McpToolName = keyof typeof mcpTools;
export { WorkItemListItem };
